import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../context/BrandContext";
import { DEMO_ACCOUNTS, DEMO_PASSWORD, isDemoMode } from "../config/demoMode";
import { DEVELOPER_CREDIT } from "../config/developer";
import ThemeToggle from "./common/ThemeToggle";
import "./LoginPage.css";

function getLoginErrorMessage(error) {
  if (!error?.response) {
    if (error?.code === "ERR_NETWORK_OFFLINE") {
      return "لا يوجد اتصال بالإنترنت — تحقق من الشبكة وحاول مرة أخرى";
    }
    if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") {
      return "الاتصال بطيء — انتظر قليلاً ثم حاول مرة أخرى";
    }
    return "تعذر الاتصال بالخادم — تحقق من الشبكة وحاول مرة أخرى";
  }

  const serverMessage = error.response?.data?.message;
  const message = Array.isArray(serverMessage)
    ? serverMessage.join(" — ")
    : typeof serverMessage === "string"
      ? serverMessage
      : "";

  if (message.includes("اشتراك الشركة منتهي")) {
    return "اشتراك الشركة منتهي — تواصل مع مسؤول المنصة لتجديد الاشتراك";
  }

  if (error.response.status === 401) {
    return "كود الموظف أو كلمة المرور غير صحيحة";
  }

  return message || "حدث خطأ أثناء تسجيل الدخول";
}

function LoginPage({ platformMode = false }) {
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuth();
  const brand = useBrand();

  const [companyCode, setCompanyCode] = useState(
    () => String(brand?.brandId || "").trim().toLowerCase(),
  );
  const [employeeCode, setEmployeeCode] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const platformLogin = platformMode;
  // No public/brand-logo.png for this brand (or it failed to load) -> fall
  // back to the pre-existing text badge below. Never a crash, never a
  // broken-image icon.
  const [logoFailed, setLogoFailed] = useState(false);

  // Already logged in (e.g. Android back landed on /login) — stay in session
  if (isAuthenticated) {
    return (
      <Navigate
        to={user?.mustChangePassword ? "/change-password" : "/"}
        replace
      />
    );
  }

  async function authenticate(rawCode, rawPassword, rawCompanyCode) {
    setError("");
    const code = String(rawCode || "").trim();
    const pass = String(rawPassword || "").trim();
    const tenantSlug = String(rawCompanyCode || companyCode || "")
      .trim()
      .toLowerCase();
    if (!platformLogin && !tenantSlug) {
      setError("من فضلك أدخل كود الشركة أو المصنع");
      return;
    }
    if (!code || !pass) {
      setError(
        platformLogin
          ? "من فضلك أدخل اسم المستخدم وكلمة المرور"
          : "من فضلك أدخل كود الموظف وكلمة المرور",
      );
      return;
    }

    setLoading(true);
    try {
      const result = await login(code, pass, {
        platform: platformLogin,
        tenantSlug: platformLogin ? undefined : tenantSlug,
      });
      if (result?.success) {
        // replace so hardware back cannot return to the login screen
        navigate(result.mustChangePassword ? "/change-password" : "/", {
          replace: true,
        });
      } else if (result?.error && typeof result.error !== "string") {
        setError(getLoginErrorMessage(result.error));
      } else {
        setError("كود الموظف أو كلمة المرور غير صحيحة");
      }
    } catch (err) {
      setError(getLoginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit() {
    authenticate(employeeCode, password, companyCode);
  }

  return (
    <div className="login-wrapper">
      <ThemeToggle variant="icon" className="ess-theme-toggle--floating" />
      <div className="login-card">
        {!logoFailed ? (
          <img
            src={brand.logoPath}
            alt={brand.companyName}
            className="login-logo-img"
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <div className="login-logo">{brand.shortName}</div>
        )}
        <p className="login-company-name" dir="ltr" lang="en">
          {brand.appName || brand.companyName}
        </p>
        <p className="login-slogan">{brand.slogan || "إدارة أذكى لفريقك"}</p>
        <p className="login-subtitle">
          {platformLogin
            ? "دخول سوبر أدمن المنصة — إدارة اشتراكات وموديولات الشركات"
            : "سجّل دخولك بكود شركتك للمتابعة إلى حسابك"}
        </p>

        <form
          className="login-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!loading) handleSubmit();
          }}
        >
        {!platformLogin ? (
          <div className="login-field">
            <label className="login-label" htmlFor="login-company-code">
              كود الشركة / المصنع
            </label>
            <input
              id="login-company-code"
              type="text"
              className="login-input"
              value={companyCode}
              onChange={(e) => setCompanyCode(e.target.value)}
              placeholder="ess"
              autoComplete="organization"
              autoCapitalize="none"
              spellCheck={false}
            />
          </div>
        ) : null}
        <div className="login-field">
          <label className="login-label" htmlFor="login-employee-code">
            {platformLogin ? "البريد أو اسم المستخدم" : "كود الموظف أو البريد"}
          </label>
          <input
            id="login-employee-code"
            type="text"
            className="login-input"
            value={employeeCode}
            onChange={(e) => setEmployeeCode(e.target.value)}
            placeholder={platformLogin ? "platform" : "EMP-01"}
            autoComplete="username"
          />
        </div>

        <div className="login-field" style={{ marginBottom: "8px" }}>
          <label className="login-label" htmlFor="login-password">
            كلمة المرور
          </label>
          <div className="login-password-wrap">
            <input
              id="login-password"
              type={showPassword ? "text" : "password"}
              className="login-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
            />
            <button
              type="button"
              className="login-password-toggle"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {error && <p className="login-error" role="alert">{error}</p>}

        <button
          type="submit"
          className={`login-button ${loading ? "is-loading" : ""}`}
          disabled={loading}
        >
          {loading ? "جاري تسجيل الدخول..." : "تسجيل الدخول"}
        </button>
        </form>
        {platformLogin ? (
          <Link to="/login" className="login-platform-link">
            رجوع لدخول الموظفين
          </Link>
        ) : null}
        {isDemoMode() && (
          <div className="login-demo-section">
            <p className="login-demo-heading">دخول تجريبي — اضغط الحساب</p>
            {DEMO_ACCOUNTS.map((account) => (
              <button
                key={account.key}
                type="button"
                className="login-demo-account"
                disabled={loading}
                onClick={() => {
                  setEmployeeCode(account.identifier);
                  setPassword(DEMO_PASSWORD);
                  authenticate(account.identifier, DEMO_PASSWORD);
                }}
              >
                <span className="login-demo-account-role">{account.label}</span>
                <span className="login-demo-account-hint">{account.hint}</span>
              </button>
            ))}
          </div>
        )}
        {brand.featureFlags?.recruitment !== false && (
          <Link to="/careers" className="login-careers-link">
            وظائف شاغرة
          </Link>
        )}
      </div>
      <p className="login-developer" dir="ltr" lang="en">
        {DEVELOPER_CREDIT}
      </p>
    </div>
  );
}

export default LoginPage;
